import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, NavLink } from "react-router-dom";
import styles from './HomePage.module.css';
import logoWhite from '../assets/logoFilledWhite.png';

const HomePage = () => {
  const [state, setState] = useState({
    isMuted: true,
    isNavLinkVisible: true,
    isFullscreen: false,
    isPortrait: window.innerHeight > window.innerWidth,
    overlayVisible: true,
  });

  const navigate = useNavigate();
  const videoRef = useRef(null);
  const overlayVideoRef = useRef(null);
  
  const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL || '';

  const videos = {
    landscape: `${mediaBaseUrl}/ST-LOUIS_SHOWREEL.mp4`,
    portrait: `${mediaBaseUrl}/ST-LOUIS_SHOWREEL_9x16.mp4`,
    intro: `${mediaBaseUrl}/logo-stlouis.mp4`,
  };

  useEffect(() => {
    if (state.overlayVisible) {
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, overlayVisible: false }));
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [state.overlayVisible]);

  const handleOrientationChange = () => {
    const isPortrait = window.innerHeight > window.innerWidth;
    setState(prev => ({ ...prev, isPortrait }));
  };

  useEffect(() => {
    window.addEventListener('resize', handleOrientationChange);
    return () => window.removeEventListener('resize', handleOrientationChange);
  }, []);

  const toggleMute = () => {
    setState(prev => ({ ...prev, isMuted: !prev.isMuted }));
  };

const handleFullscreen = async () => {
  const video = videoRef.current;
  if (!video) return;

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  try {
    if (isMobile) {
      if (isIOS) {
        if (video.webkitEnterFullscreen && typeof video.webkitEnterFullscreen === 'function') {
          video.controls = true;
          await video.webkitEnterFullscreen();
          setTimeout(() => {
            if (video) {
              video.controls = false;
            }
          }, 100);
        } else if (video.webkitRequestFullscreen && typeof video.webkitRequestFullscreen === 'function') {
          await video.webkitRequestFullscreen();
        } else {
          if (video.requestFullscreen) {
            await video.requestFullscreen();
          }
        }
      } else {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
          setState(prev => ({ ...prev, isFullscreen: false }));
        } else {
          if (video.requestFullscreen) {
            await video.requestFullscreen();
            setState(prev => ({ ...prev, isFullscreen: true }));
          } else if (video.webkitRequestFullscreen) {
            await video.webkitRequestFullscreen();
            setState(prev => ({ ...prev, isFullscreen: true }));
          }
        }
      }
    } else {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setState(prev => ({ ...prev, isFullscreen: false }));
      } else {
        await video.requestFullscreen();
        setState(prev => ({ ...prev, isFullscreen: true }));
      }
    }
  } catch (error) {
    console.error('Error attempting to toggle full-screen mode:', error);
    
    if (video.paused) {
      try {
        await video.play();
      } catch (playError) {
        console.error('Error playing video after fullscreen failure:', playError);
      }
    }
  }
};

    useEffect(() => {
        const video = videoRef.current;
        const handleLoadedMetadata = () => setRandomStartTime();
        if (video) {
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
    }, []);

    const setRandomStartTime = () => {
        const video = videoRef.current;
        if (video) {
        const duration = video.duration;
        const randomTime = Math.random() * (duration * (2 / 3)); // Start within the first 2/3 of the video
        video.currentTime = randomTime;
        }
    };

  const handlePageClick = (e) => {
    const isOutsideClick = !e.target.closest(`.${styles.muteIcon}`) && !e.target.closest(`.${styles.fullScreenIcon}`) && !e.target.closest(`.${styles.seeMore}`);
    if (isOutsideClick) {
      navigate('/work');
    }
  };

  useEffect(() => {
    document.addEventListener('click', handlePageClick);
    return () => {
      document.removeEventListener('click', handlePageClick);
    };
  }, []);

  const renderControls = () => (
    <div className={styles.buttonWrapper}>
      <div className={styles.muteIcon} onClick={toggleMute} style={{ cursor: 'pointer' }}>
        <i className={`fa-solid ${state.isMuted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
      </div>
      <div className={styles.fullScreenIcon} onClick={handleFullscreen} style={{ cursor: 'pointer' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <rect width="75" height="25" x="0" y="20" fill="white" />
          <rect width="75" height="25" x="125" y="20" fill="white" />
          <rect width="75" height="25" x="0" y="155" fill="white" />
          <rect width="75" height="25" x="125" y="155" fill="white" />
          <rect width="25" height="65" x="175" y="20" fill="white" />
          <rect width="25" height="65" x="0" y="20" fill="white" />
          <rect width="25" height="65" x="175" y="115" fill="white" />
          <rect width="25" height="65" x="0" y="115" fill="white" />
        </svg>
      </div>
    </div>
  );

  const renderNavLink = () => (
    <div className={`${styles.content} ${state.isNavLinkVisible ? styles.visible : styles.hidden}`}>
      <NavLink to="/work" className={`${styles.seeMore} ${state.isNavLinkVisible ? styles.visible : styles.hidden}`}>
        <img className={styles.logoImg} src={logoWhite} alt="Logo" />
      </NavLink>
    </div>
  );

  return (
    <div className={styles.homePage}>
      <div className={styles.videoContainer}>
        <video
          className={styles.backgroundVideo}
          autoPlay
          muted={state.isMuted}
          loop
          playsInline
          ref={videoRef}
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            background: '#000',
          }}
        >
          <source src={state.isPortrait ? videos.portrait : videos.landscape} type="video/mp4" />
          <p>St. Louis TV, post production company based in Paris.</p>
        </video>
      </div>
      <div
        className={styles.overlayVideoContainer}
        style={{
          opacity: state.overlayVisible ? 1 : 0,
          transition: 'opacity 0.5s ease-out',
          pointerEvents: 'none',
        }}
      >
        <video
          ref={overlayVideoRef}
          autoPlay
          muted
          playsInline
          onEnded={() => setState(prev => ({ ...prev, overlayVisible: false }))}
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          }}
        >
          <source src={videos.intro} type="video/mp4" />
        </video>
      </div>
      <div className={styles.footWrapper}>
        {renderNavLink()}
        {renderControls()}
      </div>
    </div>
  );
};

export default HomePage;
